import { useState, useEffect, useCallback } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Login from '@/components/Login';
import CashierDashboard from '@/components/CashierDashboard';
import AdminDashboard from '@/components/AdminDashboard';
import CEODashboard from '@/components/CEODashboard';
import DayClosingForm from '@/components/DayClosingForm';
import ViewDayClosings from '@/components/ViewDayClosings';
import Reports from '@/components/Reports';
import CashFlowStatement from '@/components/CashFlowStatement';
import ReconciliationDashboard from '@/components/ReconciliationDashboard';
import BankManagement from '@/components/BankManagement';
import BankLedger from '@/components/BankLedger';
import ExpenseLedger from '@/components/ExpenseLedger';
import ExpensePipeline from '@/components/ExpensePipeline';
import CreditLedger from '@/components/CreditLedger';
import OpeningLiabilities from '@/components/OpeningLiabilities';
import PayBills from '@/components/PayBills';
import VendorAgingReport from '@/components/VendorAgingReport';
import CreditCardBills from '@/components/CreditCardBills';
import SalaryMaster from '@/components/SalaryMaster';
import PayrollReport from '@/components/PayrollReport';
import PriceMaster from '@/components/PriceMaster';
import BarMaster from '@/components/BarMaster';
import KitchenMaster from '@/components/KitchenMaster';
import CafeMaster from '@/components/CafeMaster';
import NAPriceMaster from '@/components/NAPriceMaster';
import StoreLedger from '@/components/StoreLedger';
import AccountantAudit from '@/components/AccountantAudit';
import CEOAudit from '@/components/CEOAudit';
import Settings from '@/components/Settings';
import Layout from '@/components/Layout';
import { Toaster } from '@/components/ui/sonner';
import { versionService } from '@/lib/versionService';
import { inactivityTracker } from '@/lib/inactivityTracker';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/api';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  // Handle auto-logout due to inactivity
  const handleAutoLogout = useCallback((reason) => {
    setUser(null);
    if (reason === 'inactivity') {
      toast.warning('You have been logged out due to 30 minutes of inactivity.', {
        duration: 5000
      });
    }
  }, []);

  // Show warning before auto-logout
  const handleInactivityWarning = useCallback((minutesRemaining) => {
    toast.warning(
      `You will be logged out in ${minutesRemaining} minutes due to inactivity. Move your mouse or press any key to stay logged in.`,
      { duration: 10000, id: 'inactivity-warning' }
    );
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      const parsedUser = JSON.parse(savedUser);
      setUser(parsedUser);
      
      // Initialize inactivity tracker for the user
      inactivityTracker.init(parsedUser, handleAutoLogout, handleInactivityWarning);
    }
    setLoading(false);

    // Initialize version checking
    versionService.init((newVersion) => {
      setUpdateAvailable(true);
      toast.info(
        <div className="flex flex-col gap-2">
          <span className="font-medium">New version available!</span>
          <button 
            onClick={() => versionService.clearCacheAndReload()}
            className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
          >
            Refresh Now
          </button>
        </div>,
        { 
          duration: Infinity,
          id: 'version-update'
        }
      );
    });

    return () => {
      versionService.destroy();
      inactivityTracker.destroy();
    };
  }, [handleAutoLogout, handleInactivityWarning]);

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    
    // Initialize inactivity tracker for the newly logged in user
    inactivityTracker.init(userData, handleAutoLogout, handleInactivityWarning);
  };

  const handleLogout = async () => {
    // Clean up inactivity tracker
    inactivityTracker.destroy();
    
    // Call backend logout endpoint to record the logout
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await fetch(`${BACKEND_URL}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/" />} />
          <Route
            path="/"
            element={
              user ? (
                user.role === 'admin' ? (
                  <AdminDashboard user={user} onLogout={handleLogout} />
                ) : (
                  <Layout user={user} onLogout={handleLogout}>
                    {user.role === 'cashier' ? <CashierDashboard user={user} /> : 
                     user.role === 'storekeeper' ? <Navigate to="/credit-ledger" /> :
                     <AdminDashboard user={user} />}
                  </Layout>
                )
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/day-closing/:closingId?"
            element={
              user && user.role !== 'accountant' ? (
                <Layout user={user} onLogout={handleLogout}>
                  <DayClosingForm user={user} />
                </Layout>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/reports"
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Reports user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/cash-flow"
            element={
              user && ['admin', 'accountant', 'partner', 'general_manager'].includes(user.role) ? (
                <Layout user={user} onLogout={handleLogout}>
                  <CashFlowStatement user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/view-closings"
            element={
              user && ['admin', 'accountant', 'partner', 'general_manager'].includes(user.role) ? (
                <Layout user={user} onLogout={handleLogout}>
                  <ViewDayClosings user={user} />
                </Layout>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/banks"
            element={
              user && ['admin', 'accountant', 'partner', 'general_manager'].includes(user.role) ? (
                <Layout user={user} onLogout={handleLogout}>
                  <BankManagement user={user} />
                </Layout>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/banks/ledger/:bankAccount"
            element={
              user && ['admin', 'accountant', 'partner', 'general_manager'].includes(user.role) ? (
                <Layout user={user} onLogout={handleLogout}>
                  <BankLedger user={user} />
                </Layout>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/expense-ledger"
            element={
              user && ['admin', 'accountant', 'partner', 'general_manager'].includes(user.role) ? (
                <Layout user={user} onLogout={handleLogout}>
                  <ExpenseLedger user={user} />
                </Layout>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/reconciliation"
            element={
              user && user.role === 'admin' ? (
                <Layout user={user} onLogout={handleLogout}>
                  <ReconciliationDashboard user={user} />
                </Layout>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/credit-ledger"
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout}>
                  <CreditLedger user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/opening-liabilities"
            element={
              user && ['accountant', 'admin', 'general_manager', 'partner'].includes(user.role) ? (
                <Layout user={user} onLogout={handleLogout}>
                  <OpeningLiabilities user={user} />
                </Layout>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/expense-pipeline"
            element={
              user && ['accountant', 'admin', 'general_manager', 'partner'].includes(user.role) ? (
                <Layout user={user} onLogout={handleLogout}>
                  <ExpensePipeline user={user} />
                </Layout>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/pay-bills"
            element={
              user && ['accountant', 'admin', 'general_manager', 'partner'].includes(user.role) ? (
                <Layout user={user} onLogout={handleLogout}>
                  <PayBills user={user} />
                </Layout>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/vendor-aging"
            element={
              user && ['admin', 'general_manager', 'accountant', 'partner'].includes(user.role) ? (
                <Layout user={user} onLogout={handleLogout}>
                  <VendorAgingReport user={user} />
                </Layout>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/salary-master"
            element={
              user && ['admin', 'general_manager', 'accountant', 'partner'].includes(user.role) ? (
                <Layout user={user} onLogout={handleLogout}>
                  <SalaryMaster user={user} />
                </Layout>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/payroll-report"
            element={
              user && ['admin', 'general_manager', 'accountant', 'partner'].includes(user.role) ? (
                <Layout user={user} onLogout={handleLogout}>
                  <PayrollReport user={user} />
                </Layout>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/price-master"
            element={
              user && ['admin', 'storekeeper', 'general_manager', 'accountant', 'partner'].includes(user.role) ? (
                <Layout user={user} onLogout={handleLogout}>
                  <PriceMaster user={user} />
                </Layout>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/na-price-master"
            element={
              user && ['admin', 'storekeeper', 'partner'].includes(user.role) ? (
                <Layout user={user} onLogout={handleLogout}>
                  <NAPriceMaster user={user} />
                </Layout>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/store-ledger"
            element={
              user && ['admin', 'storekeeper', 'partner'].includes(user.role) ? (
                <Layout user={user} onLogout={handleLogout}>
                  <StoreLedger user={user} />
                </Layout>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/bar-master"
            element={
              user && ['admin', 'bartender', 'partner'].includes(user.role) ? (
                <Layout user={user} onLogout={handleLogout}>
                  <BarMaster user={user} />
                </Layout>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/kitchen-master"
            element={
              user && ['admin', 'kitchen', 'partner'].includes(user.role) ? (
                <Layout user={user} onLogout={handleLogout}>
                  <KitchenMaster user={user} />
                </Layout>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/cafe-master"
            element={
              user && ['admin', 'cafe', 'partner'].includes(user.role) ? (
                <Layout user={user} onLogout={handleLogout}>
                  <CafeMaster user={user} />
                </Layout>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/credit-cards"
            element={
              user && ['admin', 'partner'].includes(user.role) ? (
                <Layout user={user} onLogout={handleLogout}>
                  <CreditCardBills user={user} />
                </Layout>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/settings"
            element={
              user && user.role === 'admin' ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Settings user={user} />
                </Layout>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/audit-checklist"
            element={
              user && ['accountant', 'admin'].includes(user.role) ? (
                <Layout user={user} onLogout={handleLogout}>
                  <AccountantAudit user={user} />
                </Layout>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/ceo-audit"
            element={
              user && user.role === 'admin' ? (
                <Layout user={user} onLogout={handleLogout}>
                  <CEOAudit />
                </Layout>
              ) : (
                <Navigate to="/" />
              )
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
